import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

const benchmarks = [
  { symbol: 'SPY', label: 'S&P 500 (SPY)', color: '#6366F1' },
  { symbol: 'QQQ', label: 'NASDAQ 100 (QQQ)', color: '#10B981' },
  { symbol: 'DIA', label: 'Dow Jones (DIA)', color: '#F59E0B' },
];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

// Timeframe options with their duration, Alpaca timeframe, and limit
const timeframeOptions = [
  { value: '1D', label: '1 Day', duration: 1, unit: 'day', alpacaTimeframe: '1Day', limit: 390 },
  { value: '1W', label: '1 Week', duration: 1, unit: 'week', alpacaTimeframe: '1Day', limit: 7 },
  { value: '1M', label: '1 Month', duration: 1, unit: 'month', alpacaTimeframe: '1Day', limit: 30 },
  { value: '3M', label: '3 Months', duration: 3, unit: 'month', alpacaTimeframe: '1Day', limit: 90 },
  { value: '6M', label: '6 Months', duration: 6, unit: 'month', alpacaTimeframe: '1Day', limit: 180 },
  { value: '1Y', label: '1 Year', duration: 1, unit: 'year', alpacaTimeframe: '1Day', limit: 252 },
  { value: '2Y', label: '2 Years', duration: 2, unit: 'year', alpacaTimeframe: '1Week', limit: 104 },
  { value: '5Y', label: '5 Years', duration: 5, unit: 'year', alpacaTimeframe: '1Month', limit: 60 },
];

const IndexBenchmarks = () => {
  const [dataMap, setDataMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('1Y');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        // Calculate start date based on timeframe
        const end = new Date(Date.now() - 60 * 60 * 1000); // 1 hour ago
        const start = new Date(end);
        
        const timeframeOption = timeframeOptions.find(opt => opt.value === timeframe);
        if (!timeframeOption) {
          setError('Invalid timeframe');
          return;
        }

        // Calculate start date based on duration and unit
        const { duration, unit } = timeframeOption;
        switch (unit) {
          case 'day':
            start.setDate(start.getDate() - duration);
            break;
          case 'week':
            start.setDate(start.getDate() - (duration * 7));
            break;
          case 'month':
            start.setMonth(start.getMonth() - duration);
            break;
          case 'year':
            start.setFullYear(start.getFullYear() - duration);
            break;
          default:
            start.setFullYear(start.getFullYear() - 1);
        }

        const params = {
          timeframe: timeframeOption.alpacaTimeframe,
          start: start.toISOString(),
          end: end.toISOString(),
          limit: timeframeOption.limit,
        };

        const results = {};
        for (const b of benchmarks) {
          const res = await marketDataApi.getBars(b.symbol, params);
          const bars = res?.data ?? res; // handle api shape
          const list = Array.isArray(bars) ? bars : [];
          const mapped = list
            .map((bar) => {
              const timestamp = bar.timestamp || bar.t || bar.time || bar.Time || bar.date;
              return {
                timestamp,
                close: bar.close ?? bar.c,
              };
            })
            .filter((p) => p.timestamp && p.close !== undefined && p.close !== null)
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          results[b.symbol] = mapped.map((p) => ({
            date: p.timestamp,
            close: p.close,
          }));
        }
        setDataMap(results);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load benchmarks');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [timeframe]);

  return (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-gray-800 flex items-center gap-1.5">
          <FontAwesomeIcon icon={faChartLine} className="text-xs" />
          Major Indices
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            disabled={loading}
          >
            {timeframeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {loading && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-1.5">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-xs" />
          {error}
        </div>
      )}

      <div className="space-y-3">
        {benchmarks.map((b) => {
          const series = dataMap[b.symbol] || [];
          return (
            <div key={b.symbol} className="border border-gray-200 rounded-lg p-2">
              <div className="flex items-center justify-between mb-1.5">
                <div className="font-semibold text-gray-800 text-xs">{b.label}</div>
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
              </div>
              {loading && series.length === 0 ? (
                <div className="h-24 flex items-center justify-center text-gray-400 text-xs">Loading...</div>
              ) : series.length === 0 ? (
                <div className="h-24 flex items-center justify-center text-gray-400 text-xs">No data</div>
              ) : (
                <div className="h-28">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" tickFormatter={formatDate} minTickGap={30} tick={{ fontSize: 9 }} />
                      <YAxis domain={['auto', 'auto']} width={40} tick={{ fontSize: 9 }} />
                      <Tooltip labelFormatter={formatDate} formatter={(v) => v.toFixed ? v.toFixed(2) : v} />
                      <Line type="monotone" dataKey="close" stroke={b.color} strokeWidth={1.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IndexBenchmarks;

